import { forwardRef, type InputHTMLAttributes } from 'react'

export interface SliderProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string
  showValue?: boolean
  valueFormatter?: (value: number) => string
  min?: number
  max?: number
  step?: number
}

export const Slider = forwardRef<HTMLInputElement, SliderProps>(
  (
    {
      label,
      showValue = true,
      valueFormatter,
      min = 0,
      max = 100,
      step = 1,
      value,
      className = '',
      id,
      disabled,
      ...props
    },
    ref,
  ) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-')
    const numericValue = typeof value === 'number' ? value : Number(value) || min
    const displayValue = valueFormatter ? valueFormatter(numericValue) : `${numericValue}`

    return (
      <div className={`flex flex-col gap-2 ${className}`}>
        {(label || showValue) && (
          <div className="flex items-center justify-between">
            {label && (
              <label htmlFor={inputId} className="text-sm font-medium text-gray-700 dark:text-white/65">
                {label}
              </label>
            )}
            {showValue && <span className="text-sm font-medium text-primary-600">{displayValue}</span>}
          </div>
        )}
        <input
          ref={ref}
          type="range"
          id={inputId}
          min={min}
          max={max}
          step={step}
          value={value}
          disabled={disabled}
          className={`
            h-2 w-full cursor-pointer appearance-none rounded-lg bg-gray-200 dark:bg-white/20
            disabled:cursor-not-allowed disabled:opacity-50
            [&::-webkit-slider-thumb]:h-4
            [&::-webkit-slider-thumb]:w-4
            [&::-webkit-slider-thumb]:appearance-none
            [&::-webkit-slider-thumb]:rounded-full
            [&::-webkit-slider-thumb]:bg-primary-600
            [&::-webkit-slider-thumb]:shadow-md
            [&::-webkit-slider-thumb]:transition-transform
            [&::-webkit-slider-thumb]:hover:scale-110
            [&::-moz-range-thumb]:h-4
            [&::-moz-range-thumb]:w-4
            [&::-moz-range-thumb]:appearance-none
            [&::-moz-range-thumb]:rounded-full
            [&::-moz-range-thumb]:border-0
            [&::-moz-range-thumb]:bg-primary-600
            [&::-moz-range-thumb]:shadow-md
          `}
          {...props}
        />
      </div>
    )
  },
)

Slider.displayName = 'Slider'
